import { Notification } from "@interfaces/notifications";
import { parseUtcTimestamp } from "@util/utc-timestamp";

/** Payload type used by the notifications manager for system status messages. */
export const SYSTEM_STATUS_PAYLOAD_TYPE = "system_status";

/** Event name stamped by the backend on release (Rilascio) campaigns. */
export const NEW_VERSION_EVENT_NAME = "NewVersionEvent";

export type SystemStatusTipologia = "release" | "maintenance" | "notice";

export interface SystemStatusFeature {
  text: string;
  url?: string;
}

export interface SystemStatusContent {
  title: string;
  description: string;
}

export interface SystemStatusLink {
  label: string;
  url: string;
}

/** Payload may arrive as an object or as a JSON string depending on the endpoint. */
export function parseNotificationPayload(message: Notification | null | undefined): Record<string, any> {
  const payload = message?.payload;
  if (!payload) {
    return {};
  }
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload) || {};
    } catch {
      return {};
    }
  }
  if (typeof payload === "object") {
    return payload as Record<string, any>;
  }
  return {};
}

export function isSystemStatusMessage(message: Notification | null | undefined): boolean {
  if (!message) {
    return false;
  }
  if (message.event_name === NEW_VERSION_EVENT_NAME) {
    return true;
  }
  const payload = parseNotificationPayload(message);
  return payload["type"] === SYSTEM_STATUS_PAYLOAD_TYPE;
}

export function getSystemStatusTipologia(message: Notification | null | undefined): SystemStatusTipologia | null {
  if (!isSystemStatusMessage(message)) {
    return null;
  }
  const tipologia = parseNotificationPayload(message)["tipologia"];
  if (tipologia === "release" || tipologia === "maintenance" || tipologia === "notice") {
    return tipologia;
  }
  return message?.event_name === NEW_VERSION_EVENT_NAME ? "release" : null;
}

export function getSystemStatusVersion(message: Notification | null | undefined): string {
  return (parseNotificationPayload(message)["version"] ?? "").toString().trim();
}

function toDate(value: unknown): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  return parseUtcTimestamp(String(value).trim());
}

/** True while the message is published (send_at reached) and not expired. */
export function isMessageWithinSchedule(message: Notification | null | undefined, now: Date = new Date()): boolean {
  if (!message) {
    return false;
  }
  const sendAt = toDate(message.send_at);
  if (sendAt && sendAt.getTime() > now.getTime()) {
    return false;
  }
  const expireAt = toDate(message.expire_at);
  if (expireAt && expireAt.getTime() <= now.getTime()) {
    return false;
  }
  return true;
}

/** Maintenance message currently in effect: published, not expired and end date still in the future. */
export function isActiveMaintenanceMessage(message: Notification | null | undefined, now: Date = new Date()): boolean {
  if (getSystemStatusTipologia(message) !== "maintenance") {
    return false;
  }
  if (!isMessageWithinSchedule(message, now)) {
    return false;
  }
  const maintenanceEndAt = toDate(parseNotificationPayload(message)["maintenanceEndAt"]);
  if (maintenanceEndAt && maintenanceEndAt.getTime() <= now.getTime()) {
    return false;
  }
  return true;
}

/** Localized title/description with fallback on English and then on the message itself. */
export function resolveLocalizedContent(
  message: Notification | null | undefined,
  locale: string
): SystemStatusContent {
  const payload = parseNotificationPayload(message);
  const multilingualContent = payload["multilingualContent"] || {};
  const content = multilingualContent[locale] || multilingualContent["en"] || {};
  return {
    title: (content.title || message?.title || "").toString(),
    description: (content.description || message?.message || "").toString(),
  };
}

/** Localized release features with fallback on English. */
export function resolveLocalizedFeatures(
  message: Notification | null | undefined,
  locale: string
): SystemStatusFeature[] {
  const features = parseNotificationPayload(message)["features"] || {};
  const list = features[locale] || features["en"] || [];
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .map((feature: any) => ({
      text: (feature?.text ?? "").toString().trim(),
      url: (feature?.url ?? "").toString().trim() || undefined,
    }))
    .filter((feature: SystemStatusFeature) => feature.text.length > 0);
}

/** Most recent release message announcing a version different from the one currently running. */
export function findReleaseMessageForVersion(
  messages: Notification[] | null | undefined,
  currentVersion: string,
  now: Date = new Date()
): Notification | null {
  const current = (currentVersion || "").toString().trim();
  const releases = (messages ?? []).filter((message) => {
    if (getSystemStatusTipologia(message) !== "release") {
      return false;
    }
    if (!isMessageWithinSchedule(message, now)) {
      return false;
    }
    const version = getSystemStatusVersion(message);
    return !!version && version !== current;
  });
  if (!releases.length) {
    return null;
  }
  return releases.sort(
    (a, b) => (toDate(b.send_at)?.getTime() ?? 0) - (toDate(a.send_at)?.getTime() ?? 0)
  )[0];
}

export function findActiveMaintenanceMessage(
  messages: Notification[] | null | undefined,
  now: Date = new Date()
): Notification | null {
  const maintenances = (messages ?? []).filter((message) => isActiveMaintenanceMessage(message, now));
  if (!maintenances.length) {
    return null;
  }
  return maintenances.sort(
    (a, b) => (toDate(b.send_at)?.getTime() ?? 0) - (toDate(a.send_at)?.getTime() ?? 0)
  )[0];
}

/** Links exposed by a system status message: CTA url plus the release feature urls. */
export function collectMessageLinks(
  message: Notification | null | undefined,
  locale: string
): SystemStatusLink[] {
  const payload = parseNotificationPayload(message);
  const links: SystemStatusLink[] = [];

  const ctaUrl = (payload["ctaUrl"] ?? "").toString().trim();
  if (ctaUrl) {
    const content = payload["multilingualContent"]?.[locale] || payload["multilingualContent"]?.["en"] || {};
    links.push({ label: (content.ctaText || ctaUrl).toString(), url: ctaUrl });
  }

  resolveLocalizedFeatures(message, locale).forEach((feature) => {
    if (feature.url) {
      links.push({ label: feature.text, url: feature.url });
    }
  });

  return links;
}
